/**
 * Prevodi Smart Measure. Jezik UI-ja: srpski latinica (ijekavica), engleski; njemački u pripremi.
 *
 * Konvencije:
 * - semantički ključevi po namespaceu (`panel:projects.table.client`), nikad rečenice kao ključevi;
 * - domenske labele (faze, stanice, uloge, statusi, kategorije...) su samo u `common`;
 * - greške API-ja su u `errors` po kodu (`errors:measurement_locked`), validacija u `errors:validation.*`;
 * - množina: sr `_one/_few/_other`, en `_one/_other`;
 * - `documents`, `emails` i `common` koristi i Go server (kopija pri buildu + go:embed).
 */
import i18next, { type i18n as I18n, type InitOptions, type Module } from 'i18next';

import enAuth from './locales/en/auth.json';
import enCommon from './locales/en/common.json';
import enDocuments from './locales/en/documents.json';
import enEmails from './locales/en/emails.json';
import enErrors from './locales/en/errors.json';
import enLanding from './locales/en/landing.json';
import enMobile from './locales/en/mobile.json';
import enPanel from './locales/en/panel.json';
import srAuth from './locales/sr/auth.json';
import srCommon from './locales/sr/common.json';
import srDocuments from './locales/sr/documents.json';
import srEmails from './locales/sr/emails.json';
import srErrors from './locales/sr/errors.json';
import srLanding from './locales/sr/landing.json';
import srMobile from './locales/sr/mobile.json';
import srPanel from './locales/sr/panel.json';

export const LANGS = [
  { code: 'sr', native: 'Srpski', english: 'Serbian', lang: 'sr-Latn', ready: true },
  { code: 'en', native: 'English', english: 'English', lang: 'en', ready: true },
  { code: 'de', native: 'Deutsch', english: 'German', lang: 'de', ready: false },
] as const;

export type LangCode = (typeof LANGS)[number]['code'];
export type LangInfo = (typeof LANGS)[number];

export const DEFAULT_LANG: LangCode = 'sr';
export const LANG_STORAGE_KEY = 'sm-lang';

export const NAMESPACES = [
  'common',
  'auth',
  'panel',
  'mobile',
  'documents',
  'emails',
  'errors',
  'landing',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const resources = {
  sr: {
    common: srCommon,
    auth: srAuth,
    panel: srPanel,
    mobile: srMobile,
    documents: srDocuments,
    emails: srEmails,
    errors: srErrors,
    landing: srLanding,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    panel: enPanel,
    mobile: enMobile,
    documents: enDocuments,
    emails: enEmails,
    errors: enErrors,
    landing: enLanding,
  },
  /** Njemački nije spreman (`ready: false`): prazni resursi, fallback na engleski. */
  de: {},
} as const;

export type Resources = (typeof resources)['sr'];

/** Jezici koje korisnik može izabrati (ready). `includeNotReady` za prikaz „Uskoro“ stavki. */
export function availableLangs(includeNotReady = false): readonly LangInfo[] {
  return LANGS.filter((l) => includeNotReady || l.ready);
}

export function isLangCode(v: unknown): v is LangCode {
  return typeof v === 'string' && LANGS.some((l) => l.code === v);
}

/** BCP 47 oznaka za `<html lang>` i `Intl` (sr -> sr-Latn). */
export function bcp47(code: LangCode): string {
  return LANGS.find((l) => l.code === code)?.lang ?? 'sr-Latn';
}

/**
 * Bira jezik: sačuvani izbor, pa jezik uređaja/pregledača (`sr`, `sr-Latn-BA`, `bs`, `hr` -> sr; `en-*` -> en),
 * pa podrazumijevani `sr`. Jezici koji nisu spremni se preskaču.
 */
export function resolveLang(...candidates: (string | null | undefined)[]): LangCode {
  for (const c of candidates) {
    if (!c) continue;
    const base = c.toLowerCase().split(/[-_]/)[0];
    const mapped = base === 'bs' || base === 'hr' || base === 'sh' || base === 'cnr' ? 'sr' : base;
    const hit = LANGS.find((l) => l.code === mapped && l.ready);
    if (hit) return hit.code;
  }
  return DEFAULT_LANG;
}

export const FALLBACK_LNG = { de: ['en', 'sr'], en: ['sr'], default: ['sr'] } as const;

export interface CreateI18nOptions {
  lng?: LangCode;
  ns?: readonly Namespace[];
  defaultNS?: Namespace;
  /** Pluginovi, npr. `initReactI18next` iz react-i18next. */
  use?: Module[];
  /** Dodatne i18next opcije (preklapaju podrazumijevane). */
  options?: Partial<InitOptions>;
}

/**
 * Kreira i inicijalizuje novu i18next instancu sa svim resursima (sinhrono, resursi su ugrađeni).
 *
 * ```ts
 * import { initReactI18next } from 'react-i18next';
 * const i18n = createI18n({ lng: resolveLang(localStorage.getItem('sm-lang'), navigator.language), use: [initReactI18next] });
 * ```
 */
export function createI18n(opts: CreateI18nOptions = {}): I18n {
  const instance = i18next.createInstance();
  for (const m of opts.use ?? []) instance.use(m);
  void instance.init({
    resources: resources as unknown as InitOptions['resources'],
    lng: opts.lng ?? DEFAULT_LANG,
    fallbackLng: FALLBACK_LNG as unknown as InitOptions['fallbackLng'],
    supportedLngs: LANGS.map((l) => l.code),
    ns: [...(opts.ns ?? NAMESPACES)],
    defaultNS: opts.defaultNS ?? 'common',
    fallbackNS: 'common',
    interpolation: { escapeValue: false },
    returnNull: false,
    initAsync: false,
    ...opts.options,
  });
  return instance;
}

/** i18n ključ za kod greške iz API-ja (`errors:<code>`), uz fallback na `errors:unknown`. */
export function errorKey(code: string | undefined | null): string {
  return code ? `errors:${code}` : 'errors:unknown';
}

/*
 * Tipizirani ključevi (opciono, u aplikaciji, ne u ovom paketu):
 *   declare module 'i18next' { interface CustomTypeOptions { defaultNS: 'common'; resources: import('@sm/i18n').Resources } }
 */
